'use client';

import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery, QueryKey } from '@tanstack/react-query';
import Loading from '@/src/components/ui/loading';

type InfiniteGridProps<T> = {
  queryKey: QueryKey;
  initialData: T[] | undefined;
  fetchFn: (pageParam: any) => Promise<T[]>;
  renderItem: (item: T, index: number) => React.ReactNode;
  gridClassName?: string;
  pageSize?: number;
  emptyComponent?: React.ReactNode;
  customLoader?: React.ReactNode;
  onDataChange?: (allItems: T[]) => void;
  getNextPageParamFn?: (lastPage: T[], allPages: T[][]) => any;
};

const InfiniteGrid = <T extends Record<string, any>>({
  queryKey,
  initialData,
  fetchFn,
  renderItem,
  gridClassName = 'grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 lg:gap-4',
  pageSize = 10,
  emptyComponent = <div className="py-10 text-center text-gray-500">لا توجد بيانات لعرضها</div>,
  customLoader,
  onDataChange,
  getNextPageParamFn,
}: InfiniteGridProps<T>) => {
  const isFetchingRef = useRef<boolean>(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, isLoading } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam }) => {
        const actualParam = pageParam ?? 1;
        return await fetchFn(actualParam);
      },
      initialData: initialData
        ? {
            pages: [initialData],
            pageParams: [1],
          }
        : undefined,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage || lastPage.length === 0 || lastPage.length < pageSize) {
          return undefined;
        }
        return getNextPageParamFn ? getNextPageParamFn(lastPage, allPages) : allPages.length + 1;
      },
      initialPageParam: 1,
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });

  const [ref] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 200px 0px',
    triggerOnce: false,
    onChange: (inViewValue) => {
      if (inViewValue && hasNextPage && !isFetchingNextPage && !isFetchingRef.current) {
        isFetchingRef.current = true;
        fetchNextPage();
      }
    },
  });

  const flatItems = useMemo(() => {
    return (data?.pages.flat().filter(Boolean) as T[]) || [];
  }, [data?.pages]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      isFetchingRef.current = false;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (!onDataChange) return;

    const timer = setTimeout(() => {
      onDataChange(flatItems);
    }, 0);

    return () => clearTimeout(timer);
  }, [flatItems, onDataChange]);

  if (isLoading) {
    return <div className="flex justify-center py-20">{customLoader || <Loading />}</div>;
  }

  if (status === 'error') {
    return <div className="py-5 text-center text-red-500">حدث خطأ أثناء تحميل البيانات</div>;
  }

  if (flatItems.length === 0) {
    return <>{emptyComponent}</>;
  }

  return (
    <section className="">
      <div className={gridClassName}>
        {data?.pages.map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {page?.map((item, itemIndex) => (
              <Fragment key={itemIndex}>{item && renderItem(item, itemIndex)}</Fragment>
            ))}
          </Fragment>
        ))}
      </div>

      {hasNextPage && (
        <div ref={ref} className="mt-4 flex h-10 w-full items-center justify-center">
          {isFetchingNextPage ? customLoader || <Loading /> : null}
        </div>
      )}
    </section>
  );
};

export default InfiniteGrid;
